import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * A page's content column.
 *
 * ── Why this no longer sets a width or a gutter ─────────────────────────────
 *
 * It used to be `mx-auto max-w-6xl px-4 sm:px-6 lg:px-8` - a centred document.
 * The design is not that: it fills the viewport, with a 24px gutter that the
 * shell owns because every screen shares it. A wide monitor gets more content
 * rather than more margin, and that difference is most of why the design reads
 * as an application and a centred column reads as a page.
 *
 * Kept as a component rather than deleted from thirty call sites, because it is
 * still where a page-level layout decision belongs - and if one screen ever
 * genuinely needs a reading measure, this is where it goes. `min-w-0` is the
 * one thing it still carries, and it is load-bearing: without it a wide table
 * inside a flex column refuses to shrink and pushes the whole page sideways.
 */
export function Container({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('w-full min-w-0', className)} {...props} />;
}
